import type { ReactNode } from "react";
import { Tooltip } from "antd";
import AccountOrderGrid, { AccountOrderMenu, type AccountOrderColumn } from "../../Shop/Orders/AccountOrderGrid.tsx";
import { t } from "../../../features/store-language/i18n.ts";

export type AccountColumn<Row> = AccountOrderColumn<Row>;
export type AccountRowAction<Row> = {
  key: string;
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  disabledReason?: (row: Row) => string | undefined;
  run: (row: Row) => void | Promise<void>;
};

// account/orders is the canonical implementation. Do not duplicate grid chrome here.
export function AccountGrid<Row extends { id: number | string }>({
  id, label, rows, columns, loading, busy = false, actions, rowActions, onRefresh, filterBy = "displayName",
}: {
  id: string; label: string; rows: Row[]; columns: AccountColumn<Row>[]; loading: boolean; busy?: boolean;
  actions?: ReactNode; rowActions: AccountRowAction<Row>[]; onRefresh: () => void; filterBy?: string;
}) {
  const operation: AccountColumn<Row> = {
    colId: "operation",
    field: "operation" as keyof Row,
    headerName: t("عملیات"),
    pinned: "left",
    sortable: false,
    filter: false,
    floatingFilter: false,
    suppressSharedOperationMenu: true,
    cellRenderer: function (params) {
      const row = params.data;
      if (!row) return null;
      return (
        <AccountOrderMenu
          pending={busy}
          disabled={loading || busy}
          items={rowActions.map(function (action) {
            const reason = action.disabledReason?.(row);
            return {
              key: action.key,
              icon: action.icon,
              label: reason ? (
                <Tooltip title={reason}>
                  <span>{action.label}</span>
                </Tooltip>
              ) : action.label,
              danger: action.danger,
              disabled: Boolean(reason),
              onClick: function () {
                if (!loading && !busy && !action.disabledReason?.(row)) return action.run(row);
              },
            };
          })}
        />
      );
    },
  };
  return (
    <section className="account-orders-page">
      <AccountOrderGrid
        id={id}
        title={label}
        filterBy={filterBy}
        rows={rows}
        columns={[...columns, operation]}
        loading={loading || busy}
        loadingTip={t("در حال آماده سازی داده ها")}
        onRefresh={onRefresh}
        actions={actions}
      />
    </section>
  );
}
